import React from 'react';
import { Box, ButtonBase, css, Dialog, DialogContent, FormControl, Grid, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import WhiteInosio from '../../../icons/whiteinosio.svg';
import BankAccountStraight from '../../../icons/BankAccountStraight.svg';
import CreditCardStraight from '../../../icons/CreditCardStraight.svg';
import DebitCardStraight from '../../../icons/DebitCardStraight.svg';
import BackIcon from '../../../icons/back1.svg';
import { CreateOneTimePaymentDto, CreateTransactionDetailDto, FeesDTO, PaidByEnum, PaymentTypeEnum, QuickPayTypeDto } from '@inosio/payments-sdk';
import NumberFormat from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { convertCentsToDollars } from '../../../shared/util/converters';
import { getBuildQuickPayOTPDto, getSelectPayOption, getQuickPaymentFees, selectQuickPaymentsState } from '../sagas/quickpaymentsSlice';
import { HashLoader } from 'react-spinners';
import { makeStyles } from '@mui/styles';
import HelpIcon from '@mui/icons-material/Help';
import { UNREG_DEBIT_CARD_GENERIC } from '../../../utils/constants';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { majorUnitsStringToMinorUnitsInt } from '../../../shared/util/formaters';

const useStyles = makeStyles((theme) => ({
  customInputStyle: {
    borderRadius: '24px !important',
    boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1) !important',
    outline: 'none !important',
    fontFamily: 'Work Sans !important',
    height: '33px  !important',
    '& legend span': {
      marginRight: '-6px',
    },
    '& input': {
      textAlign: 'center',
    },
  },
}));

interface QuickPayMethodsDialogProps {
  open: boolean;
  onClickCardIcon: () => void;
  onClickBankIcon: () => void;
  onBackClick: () => void;
  quickPayItem?: QuickPayTypeDto;
}

const QuickPayMethodsDialog = (props: QuickPayMethodsDialogProps) => {
  const { open, onClickCardIcon, onClickBankIcon, onBackClick, quickPayItem } = props;
  const isOnMobileScreen = useMediaQuery('(max-width:600px)');
  const dispatch = useDispatch();
  const classes = useStyles();

  // const { companyResponse } = useSelector(selectApplicationsState);
  const { propertyGroupDetails: PropertyGroupDetails } = useSelector(selectPropertyGroupDetailsState);
  const { isFetchingQuickPaymentFees, quickPaymentFees, genericPayerResponse, propertyAddress } = useSelector(selectQuickPaymentsState);
  const [availableQPFees, setAvailableQPFees] = React.useState<FeesDTO[]>([]);
  const cc_fee = quickPaymentFees?.find((f) => f.PaymentType === PaymentTypeEnum.Cc);
  React.useEffect(() => {
    const fees = quickPaymentFees.filter((payoption) => payoption.PaymentClass === 'OTP' && quickPayItem?.AllowedPaymentTypes.includes(payoption.PaymentType.toString()));
    setAvailableQPFees(fees);
  }, [quickPaymentFees, quickPayItem]);

  const [payAmount, setPayAmount] = React.useState(0);
  const [searchTimer, setSearchTimer] = React.useState<NodeJS.Timeout>();

  const callFeesEndpoint = React.useCallback(() => {
    dispatch({
      type: getQuickPaymentFees.type,
      payload: {
        PayerUUID: genericPayerResponse?.Uuid ?? '',
        PaymentAmount: payAmount,
        PropPayTypeId: quickPayItem?.EntityPaymentTypeId ?? 0,
      },
    });
  }, [dispatch, payAmount, genericPayerResponse, quickPayItem]);

  //getting the fees
  React.useEffect(() => {
    if (quickPayItem && genericPayerResponse?.Uuid && !showMinimumWarning) {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
      const timer = setTimeout(() => callFeesEndpoint(), 1000);
      setSearchTimer(timer);
    }
  }, [dispatch, payAmount, quickPayItem, genericPayerResponse]);

  const style = {
    minHeight: isOnMobileScreen ? '100%' : '523px',
  };

  const override = css`
    display: block;
    margin: 40% auto;
  `;

  const [showMinimumWarning, setShowMinimumWarning] = React.useState(false);
  const [showMaximumWarning, setShowMaximumWarning] = React.useState(false);

  const handlePayAmountInput = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValueMinorUnits = majorUnitsStringToMinorUnitsInt(e.target.value) || 0;
    if (newValueMinorUnits !== payAmount) setPayAmount(newValueMinorUnits);
    if (newValueMinorUnits < (quickPayItem?.MinimumPaymentAmount ?? 0)) {
      setShowMinimumWarning(true);
    } else {
      setShowMinimumWarning(false);
    }

    if (newValueMinorUnits > (quickPayItem?.MaximumPaymentAmount ?? 0)) {
      setShowMaximumWarning(true);
    } else {
      setShowMaximumWarning(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullScreen={isOnMobileScreen}
      BackdropProps={{
        invisible: true,
      }}
      fullWidth={true}
      disableEnforceFocus
      style={{
        background: 'none',
      }}
      PaperProps={{
        style: {
          pointerEvents: 'auto',
          borderRadius: isOnMobileScreen ? '0px' : '24px',
          width: isOnMobileScreen ? '100%' : '472px',
          boxShadow: 'none',
          overflow: 'hidden',
        },
      }}
      disableEscapeKeyDown={true}
    >
      <DialogContent>
        <Box sx={style}>
          <Grid container display="flex" flexDirection="column">
            {isOnMobileScreen ? (
              <Grid item display="flex" justifyContent="center">
                <Grid item display="flex" pl={4} sx={{ width: '340px' }}>
                  <img src={WhiteInosio} style={{ width: '75px', height: '75px' }} alt="logo" />
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '18px',
                      lineHeight: '23px',
                      color: '#14142B',
                      textAlign: 'left',
                      padding: '15px',
                    }}
                  >
                    {PropertyGroupDetails?.CompanyName}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <></>
            )}

            <Box sx={{ width: '100%' }}>
              <Typography
                sx={{
                  fontFamily: 'Open Sans',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '18px',
                  lineHeight: '25px',
                  color: '#14142B',
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '20px',
                }}
              >
                Enter Payment Amount
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mx: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Work Sans',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    fontSize: '24px',
                    lineHeight: '22px',
                    color: '#A8A8A8',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    width: 'calc( 100% - 135px )',
                  }}
                >
                  {quickPayItem?.Label ?? 'Quick Pay'}
                </Typography>

                <FormControl fullWidth error={false} sx={{ width: 127 }}>
                  <TextField
                    name={'PayAmount'}
                    label={''}
                    onChange={handlePayAmountInput}
                    value={payAmount / 100}
                    InputProps={{
                      className: classes.customInputStyle,
                      inputComponent: NumberFormatCustom as any,
                    }}
                  />
                </FormControl>
              </Box>

              {showMinimumWarning && (
                <Typography
                  sx={{
                    fontFamily: 'Wotk Sans',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '18px',
                    color: '#DB3939',
                    textAlign: 'right',
                    marginTop: '5px',
                    marginRight: '10px',
                  }}
                >
                  {`Minimum amount is $${convertCentsToDollars(quickPayItem?.MinimumPaymentAmount ?? 0)}`}
                </Typography>
              )}

              {showMaximumWarning && (
                <Typography
                  sx={{
                    fontFamily: 'Wotk Sans',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '18px',
                    color: '#DB3939',
                    textAlign: 'right',
                    marginTop: '5px',
                    marginRight: '10px',
                  }}
                >
                  {`Maximum amount is $${convertCentsToDollars(quickPayItem?.MaximumPaymentAmount ?? 0)}`}
                </Typography>
              )}

              {isFetchingQuickPaymentFees ? (
                <Box sx={{ width: '100%' }}>
                  <HashLoader color={'#D3D3D3'} loading={true} size={100} css={override} />
                </Box>
              ) : (
                <Box sx={{ width: '100%' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Open Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '18px',
                      lineHeight: '25px',
                      color: '#14142B',
                      display: 'flex',
                      justifyContent: 'center',
                      mt: 5,
                    }}
                  >
                    Choose Payment Method
                  </Typography>

                  <Grid container rowSpacing={2} mt={3}>
                    {availableQPFees.map((option, i, row) => {
                      return (
                        <Grid item sm={i + 1 === row.length || row.length < 3 ? 12 : 6} xs={12} key={option.PaymentType} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <ButtonBase
                            onClick={() => {
                              option.PaymentType === 'ACH' ? onClickBankIcon() : onClickCardIcon();
                              const payerFeeAmount = option.PaidBy === PaidByEnum.Pm ? 0 : option.FeeAmount;
                              dispatch({
                                type: getBuildQuickPayOTPDto.type,
                                payload: {
                                  PayerUuid: genericPayerResponse?.Uuid ?? '',
                                  TransactionDetails: [
                                    {
                                      Amount: payAmount,
                                      PropertyPaymentTypeId: quickPayItem?.EntityPaymentTypeId ?? 0,
                                    } as CreateTransactionDetailDto,
                                  ],
                                  FeeAmount: payerFeeAmount,
                                  TotalAmount: payAmount + payerFeeAmount,
                                  Memo: propertyAddress ? `Quick Pay: ${propertyAddress}` : 'Quick Pay',
                                } as Partial<CreateOneTimePaymentDto>,
                              });
                              dispatch({
                                type: getSelectPayOption.type,
                                payload: option.PaymentType,
                              });
                            }}
                            disabled={!payAmount || showMinimumWarning ||showMaximumWarning}
                          >
                            <img
                              id={option.PaymentType === 'CC' ? 'credit-Card-Id' : option.PaymentType === 'DB' ? 'debit-Card-Id' : 'bank-Account-Id'}
                              src={option.PaymentType === 'CC' ? CreditCardStraight : option.PaymentType === 'DB' ? DebitCardStraight : BankAccountStraight}
                              alt="paytype"
                            />
                          </ButtonBase>
                          {option?.PaidBy === PaidByEnum.Pm ? (
                            <Typography>Free</Typography>
                          ) : (
                            <Grid item display="flex" alignItems="center">
                              <Typography>
                                fee
                                {option.PaymentType === PaymentTypeEnum.Db && (
                                  <Tooltip leaveTouchDelay={5000} enterTouchDelay={0} title={UNREG_DEBIT_CARD_GENERIC} placement="bottom">
                                    <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
                                  </Tooltip>
                                )}
                                : &nbsp;
                              </Typography>
                              <NumberFormat
                                thousandsGroupStyle="thousand"
                                value={convertCentsToDollars(option?.FeeAmount ? option?.FeeAmount : 0)}
                                prefix="$"
                                decimalSeparator="."
                                displayType="text"
                                type="text"
                                thousandSeparator={true}
                                allowNegative={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                              />
                              {option.PaymentType === 'DB' && cc_fee && (
                                <>
                                  &nbsp; - &nbsp;
                                  <NumberFormat
                                    thousandsGroupStyle="thousand"
                                    value={convertCentsToDollars(cc_fee.FeeAmount)}
                                    prefix="$"
                                    decimalSeparator="."
                                    displayType="text"
                                    type="text"
                                    thousandSeparator={true}
                                    allowNegative={true}
                                    decimalScale={2}
                                    fixedDecimalScale={true}
                                  />
                                </>
                              )}
                            </Grid>
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}
            </Box>
          </Grid>
        </Box>
        <Grid item sx={{ position: 'absolute', top: isOnMobileScreen ? 115 : 40, left: 15 }}>
          <img src={BackIcon} alt="back" style={{ cursor: 'pointer' }} onClick={onBackClick} />
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default QuickPayMethodsDialog;

interface CustomAmountProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

export const NumberFormatCustom = React.forwardRef<NumberFormat<any>, CustomAmountProps>(function NumberFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      fixedDecimalScale
      decimalScale={2}
      prefix="$"
    />
  );
});
